import React from 'react';
import * as htmlToImage from 'html-to-image';
import { jsPDF } from 'jspdf';

export function getDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function truncate(str, n) {
  if (!str) return '';
  return str.length > n ? str.substring(0, n) + '…' : str;
}

export function InvoiceCopy({ order }) {
  const items = order.orderItems || [];
  const sealSrc = order.isPaid ? '/paidSeal.png' : '/dueSeal.png';

  return (
    <section className="invc-copy">
      <header className="invc-header">
        <div className="invc-brand">
          <img
            src="https://res.cloudinary.com/dddogyqqa/image/upload/v1670435815/logo/clickcart-logo_wfktlg.png"
            alt="ClickCart"
            className="invc-logo"
            crossOrigin="anonymous"
          />
          <img
            src={sealSrc}
            alt={order.isPaid ? 'PAID' : 'DUE'}
            className="invc-seal"
            crossOrigin="anonymous"
          />
        </div>
        <div className="invc-title-block">
          <div className="invc-title">INVOICE</div>
          <span className="invc-id">#{order._id.slice(-6).toUpperCase()}</span>
        </div>
      </header>

      <div className="invc-body">
        <aside className="invc-customer">
          {order.user?.profilePhoto && (
            <img
              src={order.user.profilePhoto}
              alt=""
              className="invc-photo"
              crossOrigin="anonymous"
            />
          )}
          <div className="invc-field">
            <b>Username</b>
            <span>{order.user?.username || '—'}</span>
          </div>
          <div className="invc-field">
            <b>Name</b>
            <span>{order.shippingAddress?.fullName || '—'}</span>
          </div>
          <div className="invc-field">
            <b>Phone</b>
            <span>{order.shippingAddress?.phone || '—'}</span>
          </div>
          <div className="invc-field">
            <b>Address</b>
            <span>{order.shippingAddress?.streetAddress || '—'}</span>
          </div>
          {order.user?.sign && (
            <img
              src={order.user.sign}
              alt=""
              className="invc-sign-img"
              crossOrigin="anonymous"
            />
          )}
        </aside>

        <div className="invc-main">
          <div className="invc-dates">
            <div>
              <b>Invoice Date</b>
              <span>{getDate(order.createdAt)}</span>
            </div>
            <div className="invc-dates-right">
              <b>Due Date</b>
              <span>{getDate(order.createdAt)}</span>
            </div>
          </div>

          <table className="invc-items">
            <thead>
              <tr>
                <th className="invc-col-name">Product Name</th>
                <th className="invc-num">Price</th>
                <th className="invc-num">QTY</th>
                <th className="invc-num">Total</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={item.slug || idx}>
                  <td>{truncate(item.name, 26)}</td>
                  <td className="invc-num">{item.price}</td>
                  <td className="invc-num">{item.quantity}</td>
                  <td className="invc-num">{item.price * item.quantity}</td>
                </tr>
              ))}
              <tr className="invc-shipping">
                <td colSpan={3}>Shipping Cost</td>
                <td className="invc-num">{order.shippingPrice}</td>
              </tr>
              <tr className="invc-total">
                <td colSpan={3}>Total</td>
                <td className="invc-num">{order.totalPrice}</td>
              </tr>
              <tr className="invc-payment">
                <td>Payment Method</td>
                <td>{order.paymentMethod}</td>
                <td colSpan={2} className="invc-num">
                  {order.isPaid ? 'Paid' : 'Not Paid'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="invc-signs">
        <div className="invc-sign">
          <div className="invc-sign-line" />
          <div className="invc-sign-label">Receiver</div>
        </div>
        <div className="invc-sign">
          <div className="invc-sign-line" />
          <div className="invc-sign-label">Accounts</div>
        </div>
        <div className="invc-sign">
          <div className="invc-sign-line" />
          <div className="invc-sign-label">Director</div>
        </div>
      </div>

      <footer className="invc-footer">
        JA-80/b, Bir Uttam A.K Khondokar Road, Badda Link Road, Badda, Dhaka
      </footer>
    </section>
  );
}

export function InvoicePageDouble({ order, innerRef, id = 'invc-page' }) {
  return (
    <div className="invc-page" id={id} ref={innerRef}>
      <InvoiceCopy order={order} />
      <div className="invc-cutline" aria-hidden="true">
        <span>✂</span>
      </div>
      <InvoiceCopy order={order} />
    </div>
  );
}

async function preloadImages(root) {
  if (!root) return;
  const imgs = Array.from(root.querySelectorAll('img'));
  await Promise.all(
    imgs.map(
      (img) =>
        new Promise((resolve) => {
          try {
            if (!img.src) return resolve();
            if (!img.crossOrigin) img.crossOrigin = 'anonymous';
            if (img.complete && img.naturalHeight > 0) return resolve();
            img.onload = () => resolve();
            img.onerror = () => resolve();
            const src = img.src;
            img.src = '';
            img.src = src;
          } catch {
            resolve();
          }
        })
    )
  );
}

export async function renderInvoicePdf(node, fileName) {
  if (!node) throw new Error('Invoice node not found');
  await preloadImages(node);
  const dataUrl = await htmlToImage.toPng(node, {
    quality: 0.98,
    pixelRatio: 2,
    cacheBust: true,
    backgroundColor: '#ffffff',
    skipFonts: true,
  });
  const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const imgProps = pdf.getImageProperties(dataUrl);
  const ratio = Math.min(
    pageWidth / imgProps.width,
    pageHeight / imgProps.height
  );
  const w = imgProps.width * ratio;
  const h = imgProps.height * ratio;
  const x = (pageWidth - w) / 2;
  const y = (pageHeight - h) / 2;
  pdf.addImage(dataUrl, 'PNG', x, y, w, h);
  pdf.save(fileName);
}
